/**
 * Module-level singleton mouse position store.
 *
 * Replaces 8+ independent window.mousemove listeners with a single shared one.
 * Listener is added lazily on first subscribe — safe for SSR and "sideEffects": false.
 * Subscribers receive raw (x, y) synchronously inside the event handler, with no
 * React state or re-render overhead in the hot path.
 */

type Listener = (x: number, y: number) => void

let _x = 0
let _y = 0
let _initialized = false
const _subs = new Set<Listener>()

function _init(): void {
  if (_initialized || typeof window === "undefined") return
  _initialized = true
  window.addEventListener(
    "mousemove",
    (e: MouseEvent) => {
      _x = e.clientX
      _y = e.clientY
      if (_subs.size > 0) _subs.forEach((fn) => fn(_x, _y))
    },
    { passive: true },
  )
}

export const mouseStore = {
  get x(): number { return _x },
  get y(): number { return _y },
  /** Subscribe to every mouse position change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    _init()
    _subs.add(fn)
    return () => { _subs.delete(fn) }
  },
} as const
